import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Grid2 from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectIsLoading } from "../store/slices/session/sessionSelector";
import { LanguageEnum } from "../store/slices/app/appTypes";
import { selectLanguage } from "../store/slices/app/appSelector";
import { forgotPassword } from "../store/slices/session/sessionSlice";
import { setLanguage } from "../store/slices/app/appSlice";
import Welcome from "../components/welcome/Welcome";
import { useNavigate } from "react-router-dom";

const LoginPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector(selectIsLoading);
  const language: LanguageEnum = useSelector(selectLanguage);

  const [email, setEmail] = useState("");

  const handleLanguageChange = (event: SelectChangeEvent<LanguageEnum>) => {
    const newLang = event.target.value as LanguageEnum;
    dispatch(setLanguage(newLang));
  };

  const handleForgotPassword = () => {
    if (!email) {
      alert("Please enter your email");
      return;
    }
    dispatch(forgotPassword(email));
  };

  return (
    <Grid2 container sx={{ height: "100vh" }}>
      <Welcome />

      <Grid2
        component="div"
        size={{ xs: 12, md: 6 }}
        sx={{
          backgroundImage: "none",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 4,
          backgroundColor: theme.palette.background.default,
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 20,
            right: 20,
          }}
        >
          <Select
            value={language}
            onChange={handleLanguageChange}
            variant="outlined"
            sx={{
              color: theme.palette.text.primary,
              "& .MuiSvgIcon-root": {
                color: theme.palette.text.primary,
              },
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <MenuItem value={LanguageEnum.EN}>{t("languages.en")}</MenuItem>
            <MenuItem value={LanguageEnum.ES}>{t("languages.es")}</MenuItem>
          </Select>
        </Box>

        <Box
          sx={{
            width: "80%",
            maxWidth: 600,
            padding: "36px",
            backgroundColor: theme.palette.neutral.main,
            borderRadius: "16px",
          }}
        >
          <Typography
            variant="h3"
            fontWeight="bold"
            gutterBottom
            color="primary"
            sx={{ mb: 2 }}
          >
            {t("forgotPassword.title")}
          </Typography>

          <Typography variant="pMedium" color="primary" sx={{ mt: "10px" }}>
            {t("forgotPassword.text")}
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Typography
              variant="pMedium"
              fontWeight="bold"
              color="primary"
              sx={{ mt: "10px" }}
            >
              Email
            </Typography>
          </Box>

          <TextField
            fullWidth
            variant="outlined"
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
            sx={{ marginTop: 1 }}
          />

          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleForgotPassword}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
            endIcon={!isLoading && <ChevronRightIcon />}
          >
            {isLoading ? t("loading") : t("forgotPassword.send")}
          </Button>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 2,
              gap: "2px",
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontSize: 14,
                fontFamily: "Nunito",
                fontWeight: 400,
                textDecoration: "underline",
                cursor: "pointer",
                mr: 2,
              }}
              onClick={() => navigate("/login")}
            >
              {t("signup.loginHere")}
            </Typography>
          </Box>
        </Box>
      </Grid2>
    </Grid2>
  );
};

export default LoginPage;
